/**
 * Build Itinerary lens helpers (pure, testable).
 *
 * The itinerary is a per-coach LENS over the shared tournament, not a prune
 * (see the plan's Data Design Decision #2): every game persists on the
 * tournament, and the coach's player selection only filters which games show
 * up in their itinerary + review. These helpers do that filtering and derive
 * the summary counts the review rail displays. They are UI-free so they can be
 * unit tested on their own and reused by later slices (Itinerary / Assign).
 */

import type {
  MatchedPlayer,
  TournamentGameDto,
} from "../../../../../types/tournament-games";

/**
 * The subset of a favorite prospect the Players step selects over. Kept minimal
 * so the helpers do not depend on the full ProspectResponse shape.
 */
export interface SelectablePlayer {
  id: string;
  firstName: string;
  lastName: string;
  position: string | null;
  gradYear: number | null;
  /** The prospect's primary (first) team name, or null when they have none. */
  primaryTeamName: string | null;
  isFavorite: boolean;
}

/**
 * A game paired with the subset of the selected players it features. Produced
 * by filterGamesBySelection so the review UI does not re-derive the match per
 * render.
 */
export interface ItineraryGame {
  game: TournamentGameDto;
  /** The selected players matched into this game (order preserved from the game). */
  matchedSelectedPlayers: MatchedPlayer[];
  /**
   * True when one of this game's teams is a team the coach follows (independent of players). Optional
   * because lightweight constructions (e.g. the Assign step grouping games) don't compute it; the
   * lens builders always set it.
   */
  followedByTeam?: boolean;
}

const NO_FOLLOWED_TEAMS: ReadonlySet<string> = new Set();
const NO_FOLLOWED_NAMES: ReadonlySet<string> = new Set();

/** Normalize a raw team name for case/space-insensitive follow matching. */
export function normalizeTeamName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Whether either side of a game is a followed team — matched by org team id
 * (linked follows) OR by raw name (name-only follows, which create no team).
 * `followedTeamNames` must already be normalized via {@link normalizeTeamName}.
 */
function isFollowedTeamGame(
  game: TournamentGameDto,
  followedTeamIds: ReadonlySet<string>,
  followedTeamNames: ReadonlySet<string> = NO_FOLLOWED_NAMES,
): boolean {
  if (
    followedTeamIds.size > 0 &&
    ((game.homeTeamId != null && followedTeamIds.has(game.homeTeamId)) ||
      (game.awayTeamId != null && followedTeamIds.has(game.awayTeamId)))
  ) {
    return true;
  }
  if (
    followedTeamNames.size > 0 &&
    (followedTeamNames.has(normalizeTeamName(game.homeTeamNameRaw)) ||
      followedTeamNames.has(normalizeTeamName(game.awayTeamNameRaw)))
  ) {
    return true;
  }
  return false;
}

/**
 * Whether a game is relevant to what the coach follows: a team they follow plays in it, OR a player
 * they follow appears in it (a game's matchedPlayers ARE the favorited/followed prospects on its
 * teams). Backs the Assign step's "Followed" tab, which scopes a big tournament's grid to the games a
 * coach actually cares about assigning staff to — "All games" reveals the rest. Pure.
 */
export function isFollowRelevantGame(
  game: TournamentGameDto,
  followedTeamIds: ReadonlySet<string>,
  followedTeamNames: ReadonlySet<string> = NO_FOLLOWED_NAMES,
): boolean {
  return (
    game.matchedPlayers.length > 0 ||
    isFollowedTeamGame(game, followedTeamIds, followedTeamNames)
  );
}

/** Roll-up counts shown in the Step 3 itinerary summary rail. */
export interface ItinerarySummary {
  playersSelected: number;
  teamsMatched: number;
  gamesFound: number;
  days: number;
  fields: number;
}

/**
 * Keep only the games that feature at least one selected player, pairing each
 * with the selected players it matches. When nothing is selected the itinerary
 * is empty (a coach with no players selected has no personalized games).
 *
 * `selectedIds` is a Set for O(1) membership; the input games are never mutated
 * and their relative order is preserved.
 */
export function filterGamesBySelection(
  games: readonly TournamentGameDto[],
  selectedIds: ReadonlySet<string>,
  followedTeamIds: ReadonlySet<string> = NO_FOLLOWED_TEAMS,
  followedTeamNames: ReadonlySet<string> = NO_FOLLOWED_NAMES,
): ItineraryGame[] {
  if (selectedIds.size === 0 && followedTeamIds.size === 0 && followedTeamNames.size === 0)
    return [];

  const result: ItineraryGame[] = [];
  for (const game of games) {
    const matchedSelectedPlayers =
      selectedIds.size === 0
        ? []
        : game.matchedPlayers.filter((p) => selectedIds.has(p.prospectId));
    const followedByTeam = isFollowedTeamGame(game, followedTeamIds, followedTeamNames);
    // A game earns a spot in the itinerary via a followed PLAYER or a followed TEAM.
    if (matchedSelectedPlayers.length > 0 || followedByTeam) {
      result.push({ game, matchedSelectedPlayers, followedByTeam });
    }
  }
  return result;
}

/**
 * Pair EVERY game with the selected players it features, without dropping any.
 * This backs the default "all games" view: the full schedule is always visible,
 * and the matched-player chips still light up on the games a coach's starred
 * players appear in. Filtering to only the matched games (the personalized lens)
 * is an opt-in the UI layers on top via `filterGamesBySelection` or by keeping
 * only the entries whose `matchedSelectedPlayers` is non-empty.
 */
export function annotateGamesWithSelection(
  games: readonly TournamentGameDto[],
  selectedIds: ReadonlySet<string>,
  followedTeamIds: ReadonlySet<string> = NO_FOLLOWED_TEAMS,
  followedTeamNames: ReadonlySet<string> = NO_FOLLOWED_NAMES,
): ItineraryGame[] {
  return games.map((game) => ({
    game,
    matchedSelectedPlayers:
      selectedIds.size === 0
        ? []
        : game.matchedPlayers.filter((p) => selectedIds.has(p.prospectId)),
    followedByTeam: isFollowedTeamGame(game, followedTeamIds, followedTeamNames),
  }));
}

/**
 * The distinct players a coach follows that appear in this tournament, by prospect
 * id, name-sorted. "Followed" is server-derived: a game's matchedPlayers ARE the
 * favorited (followed) players on its teams, so this unions them across all games.
 * Backs the read-only "who you're following" review and seeds the itinerary lens.
 */
export function distinctFollowedPlayers(
  games: readonly TournamentGameDto[],
): MatchedPlayer[] {
  const byId = new Map<string, MatchedPlayer>();
  for (const g of games) {
    for (const p of g.matchedPlayers) {
      if (!byId.has(p.prospectId)) byId.set(p.prospectId, p);
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** A rough breakdown of what the importer parsed out, shown before team assignment. */
export interface ParseSummary {
  games: number;
  teams: number;
  days: number;
  fields: number;
  /** First/last day the games span (YYYY-MM-DD in each game's own tz), or null when empty. */
  firstDay: string | null;
  lastDay: string | null;
}

/**
 * Summarize what was parsed from the schedule: game count, distinct teams (raw
 * names), distinct days + their span, and distinct fields. Pure/testable; used
 * for the "here's what we found" panel before the coach starts matching teams.
 */
export function summarizeParse(
  games: readonly TournamentGameDto[],
): ParseSummary {
  const fields = new Set<string>();
  const days = new Set<string>();
  for (const g of games) {
    if (g.fieldName && g.fieldName.trim()) fields.add(g.fieldName.trim().toLowerCase());
    const day = gameDayKey(g);
    if (day) days.add(day);
  }
  const sortedDays = [...days].sort();
  return {
    games: games.length,
    teams: distinctTournamentTeams(games).length,
    days: days.size,
    fields: fields.size,
    firstDay: sortedDays[0] ?? null,
    lastDay: sortedDays[sortedDays.length - 1] ?? null,
  };
}

/** A distinct team the import picked up, and the org team it's currently linked to (if any). */
export interface TournamentTeamRef {
  /** The raw extracted team name (the association key). */
  rawName: string;
  /** The org team id currently linked on the games, or null when unmatched/new. */
  teamId: string | null;
}

/**
 * The distinct teams across a tournament's games (Home ∪ Away), each with the
 * org team id currently linked to it. First-seen order by raw name; a non-null
 * link wins if the same name appears both linked and unlinked across games.
 * Backs the team-association panel (assign an existing team to an imported one).
 */
export function distinctTournamentTeams(
  games: readonly TournamentGameDto[],
): TournamentTeamRef[] {
  const byName = new Map<string, string | null>();
  const order: string[] = [];
  const consider = (rawName: string, teamId: string | null) => {
    const name = rawName.trim();
    if (!name) return;
    if (!byName.has(name)) {
      byName.set(name, teamId);
      order.push(name);
    } else if (byName.get(name) == null && teamId) {
      byName.set(name, teamId);
    }
  };
  for (const g of games) {
    consider(g.homeTeamNameRaw, g.homeTeamId);
    consider(g.awayTeamNameRaw, g.awayTeamId);
  }
  return order.map((rawName) => ({ rawName, teamId: byName.get(rawName) ?? null }));
}

/**
 * The UTC calendar day (YYYY-MM-DD) a game falls on, in its OWN timezone, used
 * to group games by day. Falls back to the raw instant's UTC date if the tz id
 * is bad so a single malformed row never collapses the grouping.
 */
export function gameDayKey(game: TournamentGameDto): string {
  const start = new Date(game.startsAtUtc);
  if (Number.isNaN(start.getTime())) return "";
  try {
    // en-CA yields an ISO-like YYYY-MM-DD, honoring the game's timezone.
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: game.timeZone || "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(start);
  } catch {
    return start.toISOString().slice(0, 10);
  }
}

/** A day's worth of itinerary games, in start order. */
export interface ItineraryDayGroup {
  /** YYYY-MM-DD day key (in the games' own timezone). */
  dayKey: string;
  games: ItineraryGame[];
}

/**
 * Group filtered itinerary games by their calendar day (in the game's own
 * timezone), days ascending and games within a day ordered by start instant
 * then sortOrder. Pure; the input is not mutated.
 */
export function groupGamesByDay(
  itineraryGames: readonly ItineraryGame[],
): ItineraryDayGroup[] {
  const byDay = new Map<string, ItineraryGame[]>();
  for (const ig of itineraryGames) {
    const key = gameDayKey(ig.game);
    const bucket = byDay.get(key);
    if (bucket) {
      bucket.push(ig);
    } else {
      byDay.set(key, [ig]);
    }
  }

  return [...byDay.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([dayKey, games]) => ({
      dayKey,
      games: [...games].sort((a, b) => {
        const at = new Date(a.game.startsAtUtc).getTime();
        const bt = new Date(b.game.startsAtUtc).getTime();
        if (at !== bt) return at - bt;
        return a.game.sortOrder - b.game.sortOrder;
      }),
    }));
}

/**
 * Derive the itinerary summary from the FILTERED set (games that include a
 * selected player). Teams and fields are counted distinctly across those games;
 * days are distinct calendar days. `playersSelected` is the count of selected
 * players (not the count matched), so a coach sees how many players they chose.
 */
export function summarizeItinerary(
  itineraryGames: readonly ItineraryGame[],
  playersSelected: number,
): ItinerarySummary {
  const teams = new Set<string>();
  const fields = new Set<string>();
  const days = new Set<string>();

  for (const { game } of itineraryGames) {
    // Prefer resolved team ids; fall back to the raw name so an unmatched team
    // still counts as a distinct team rather than vanishing.
    teams.add(game.homeTeamId ?? `raw:${game.homeTeamNameRaw}`);
    teams.add(game.awayTeamId ?? `raw:${game.awayTeamNameRaw}`);
    if (game.fieldName) fields.add(game.fieldName);
    const day = gameDayKey(game);
    if (day) days.add(day);
  }

  return {
    playersSelected,
    teamsMatched: teams.size,
    gamesFound: itineraryGames.length,
    days: days.size,
    fields: fields.size,
  };
}

/**
 * Two-letter uppercase initials from a full display name, e.g.
 * "Michael Geagione" -> "MG". A single-token name yields its first letter; an
 * empty name yields "?". Used for both the player table and the review chips so
 * initials never drift between the two.
 */
export function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
  return `${first}${last}`.toUpperCase();
}

/** Initials from a separate first + last name, e.g. ("Michael", "Geagione") -> "MG". */
export function playerInitials(firstName: string, lastName: string): string {
  return initialsFromName(`${firstName} ${lastName}`);
}

/** A human day heading, e.g. "Friday, June 5, 2026", from a YYYY-MM-DD key. */
export function formatDayHeading(dayKey: string, locale = "en-US"): string {
  if (!dayKey) return "Unknown date";
  // Parse as UTC noon so the label is not shifted by the viewer's timezone.
  const d = new Date(`${dayKey}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return dayKey;
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}
